import { useAuth } from "@clerk/clerk-react";
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  AddOnDetails,
  AddOnItemName,
  CustomDomainInfo,
  Subscription,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";

import { OrganizationUserContext } from "./Organization";

export enum SubscriptionTab {
  yearly = "yearly",
  monthly = "monthly",
}

export enum SplashPage {
  instagram = "instagram",
  ooo = "ooo",
  pro_bundle = "pro_bundle",
  va_money_back = "va_money_back",
  repitching = "repitching",
  pro = "pro",
  va = "va",
  va_bundled = "va_bundled",
  strategy_package = "strategy_package",
  launchpad = "launchpad",
}

export interface CheckoutState {
  plan?: Plans | AddOnItemName;
  pageSource?: string | null;
  source?: string | null;
  coupon?: string | null;
  domainInfo?: CustomDomainInfo;
  splashPageSource?: SplashPage | null;
  conciergeOption?: number | null;
}

export type AllPlans = {
  [key in Plans]: {
    display_name: string;
    monthly_limit: number;
    price: number;
    chat_monthly_limit: number;
    brand_requests_monthly_limit: number;
    campaign_limit: number;
    description: string;
    product: string;
    terms_and_conditions?: string;
    price_annotation?: string;
  };
};

interface SubscriptionContextInterface {
  subscription: Subscription | null;
  setSubscription: React.Dispatch<SetStateAction<Subscription | null>>;
  canMakeBrandRequest: () => boolean;
  clearSubscriptionContext: () => void;
  plans: AllPlans | null;
  addOns: { [key: string]: AddOnDetails };
  fetchPlans: () => void;
  decreaseBrandRequestsCount: () => void;
  isProPlan: boolean;
  isFreePlan: boolean;
  isStarterPlan: boolean;
  isVaPlan: boolean;
  isConciergePlan: boolean;
  referralCode: string | null;
  setReferralCode: React.Dispatch<SetStateAction<string | null>>;
  setUpgradeDialogSource: React.Dispatch<
    SetStateAction<UpgradeDialogSource | null>
  >;
  upgradeDialogSource: UpgradeDialogSource | null;
  emailSents: number;
  brandRequestsMade: number;
  customizedMessagesDrafted: number;
  tab: SubscriptionTab;
  setTab: Dispatch<SetStateAction<SubscriptionTab>>;
  openCheckoutDialog: CheckoutState | null;
  setOpenCheckoutDialog: React.Dispatch<SetStateAction<CheckoutState | null>>;
  referralDialog: boolean;
  setReferralDialog: React.Dispatch<SetStateAction<boolean>>;
  customDomainDialog: boolean;
  setCustomDomainDialog: React.Dispatch<SetStateAction<boolean>>;
  cancelDialog: boolean;
  setCancelDialog: React.Dispatch<SetStateAction<boolean>>;
  manageDialog: boolean;
  setManageDialog: React.Dispatch<SetStateAction<boolean>>;
  plan: Plans | AddOnItemName | null;
  setPlan: React.Dispatch<SetStateAction<Plans | AddOnItemName | null>>;
  hasPastDue: boolean;
  setHasPastDue: React.Dispatch<SetStateAction<boolean>>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  subscription: null,
  setSubscription: defaultContextMissingFunction,
  updateSubscription: defaultContextMissingFunction,
  canMakeBrandRequest: defaultContextMissingFunction,
  clearSubscriptionContext: defaultContextMissingFunction,
  plans: null,
  addOns: {},
  fetchPlans: defaultContextMissingFunction,
  decreaseBrandRequestsCount: defaultContextMissingFunction,
  isProPlan: false,
  isFreePlan: true,
  isStarterPlan: false,
  isVaPlan: false,
  isConciergePlan: false,
  referralCode: null,
  setReferralCode: defaultContextMissingFunction,
  upgradeDialogSource: null,
  setUpgradeDialogSource: defaultContextMissingFunction,
  emailSents: 0,
  brandRequestsMade: 0,
  customizedMessagesDrafted: 0,
  tab: SubscriptionTab.yearly,
  setTab: defaultContextMissingFunction,
  openCheckoutDialog: null,
  setOpenCheckoutDialog: defaultContextMissingFunction,
  referralDialog: false,
  setReferralDialog: defaultContextMissingFunction,
  customDomainDialog: false,
  setCustomDomainDialog: defaultContextMissingFunction,
  cancelDialog: false,
  setCancelDialog: defaultContextMissingFunction,
  manageDialog: false,
  setManageDialog: defaultContextMissingFunction,
  plan: null,
  setPlan: defaultContextMissingFunction,
  hasPastDue: false,
  setHasPastDue: defaultContextMissingFunction,
};

const SubscriptionContext =
  createContext<SubscriptionContextInterface>(defaultInterface);

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plan, setPlan] = useState<Plans | AddOnItemName | null>(null);
  const [upgradeDialogSource, setUpgradeDialogSource] =
    useState<UpgradeDialogSource | null>(null);
  const [plans, setPlans] = useState<AllPlans | null>(null);
  const [addOns, setAddOns] = useState<{
    [key: string]: AddOnDetails;
  }>({});
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [tab, setTab] = useState<SubscriptionTab>(SubscriptionTab.yearly);
  const [openCheckoutDialog, setOpenCheckoutDialog] =
    useState<CheckoutState | null>(null);
  const [referralDialog, setReferralDialog] = useState<boolean>(false);
  const [customDomainDialog, setCustomDomainDialog] = useState<boolean>(false);
  const [cancelDialog, setCancelDialog] = useState<boolean>(false);
  const [manageDialog, setManageDialog] = useState<boolean>(false);
  const [hasPastDue, setHasPastDue] = useState<boolean>(false);

  const [searchParams] = useSearchParams();

  const clearSubscriptionContext = () => {
    setSubscription(null);
  };

  const canMakeBrandRequest = () => {
    return Boolean(subscription && subscription.remainingBrandRequests > 0);
  };

  const fetchPlans = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/subscription-plans`,
      );
      setPlans(res);
    } catch (error) {}
  };

  const fetchAddOns = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments-add-ons`,
      );
      setAddOns(res);
    } catch (error) {}
  };

  const fetchReferralCode = async () => {
    try {
      const { referralCode } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/referrals/code`,
      );
      setReferralCode(referralCode);
    } catch (error) {}
  };

  const decreaseBrandRequestsCount = () => {
    setSubscription((s) => {
      if (s) {
        return {
          ...s,
          remainingBrandRequests: s.remainingBrandRequests - 1,
        };
      } else {
        return s;
      }
    });
  };

  const trackGoogleAdEvent = (sessionId: string) => {
    let value =
      plans?.[subscription?.subscription?.planName as Plans]?.price ?? 10;
    if (subscription?.subscription?.planName?.includes("yearly")) {
      value = value * 12;
    }
    // @ts-ignore
    window.gtag("event", "conversion", {
      send_to: "AW-11491430223/0se8CL2UlfYZEM-execq",
      value,
      currency: "USD",
      transaction_id: sessionId,
    });
  };

  const isStarterPlan = useMemo(() => {
    return (
      subscription?.subscription?.planName === Plans.STARTER ||
      subscription?.subscription?.planName === Plans.STARTER_YEARLY
    );
  }, [subscription]);

  const isProPlan = useMemo(() => {
    return (
      subscription?.subscription?.planName === Plans.PRO ||
      subscription?.subscription?.planName === Plans.PRO_YEARLY ||
      isStarterPlan
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  const isFreePlan = useMemo(() => {
    return subscription?.subscription?.planName === Plans.FREE;
  }, [subscription]);

  const isVaPlan = useMemo(() => {
    return (
      subscription?.subscription?.planName === Plans.VA ||
      subscription?.subscription?.planName === Plans.VA_YEARLY
    );
  }, [subscription]);

  const isConciergePlan = useMemo(() => {
    return (
      subscription?.subscription?.planName === Plans.CONCIERGE ||
      subscription?.subscription?.planName === Plans.CONCIERGE_YEARLY
    );
  }, [subscription]);

  const emailSents = subscription?.monthlyLimit
    ? subscription?.monthlyLimit - subscription?.remainingMessages
    : 0;
  const brandRequestsMade = subscription?.brandRequestsMonthlyLimit
    ? subscription?.brandRequestsMonthlyLimit -
      subscription?.remainingBrandRequests
    : 0;
  const customizedMessagesDrafted = subscription?.chatMonthlyLimit
    ? subscription?.chatMonthlyLimit - subscription?.remainingChatMessages
    : 0;

  useEffect(() => {
    if (currentOrg?.id) {
      fetchPlans();
      fetchReferralCode();
      fetchAddOns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const updateTab =
    subscription?.subscription?.planName && isPage(routes.settings);
  useEffect(() => {
    if (updateTab) {
      const isMonthly =
        subscription?.subscription?.planName === Plans.PRO ||
        subscription?.subscription?.planName === Plans.STARTER ||
        subscription?.subscription?.planName === Plans.VA ||
        subscription?.subscription?.planName === Plans.CONCIERGE;
      if (
        isMonthly &&
        tab === SubscriptionTab.yearly &&
        isPage(routes.settings)
      ) {
        setTab(SubscriptionTab.monthly);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTab]);

  useEffect(() => {
    const checkoutInfo = searchParams.get("checkout");
    if (checkoutInfo === "success" && subscription?.subscription.id && plans) {
      trackGoogleAdEvent(searchParams.get("session_id") || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, subscription?.subscription.id, plans]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        setSubscription,
        upgradeDialogSource,
        setUpgradeDialogSource,
        canMakeBrandRequest,
        clearSubscriptionContext,
        plans,
        addOns,
        fetchPlans,
        decreaseBrandRequestsCount,
        isProPlan,
        isFreePlan,
        isStarterPlan,
        isVaPlan,
        isConciergePlan,
        referralCode,
        setReferralCode,
        emailSents,
        brandRequestsMade,
        customizedMessagesDrafted,
        tab,
        setTab,
        openCheckoutDialog,
        setOpenCheckoutDialog,
        referralDialog,
        setReferralDialog,
        customDomainDialog,
        setCustomDomainDialog,
        cancelDialog,
        setCancelDialog,
        manageDialog,
        setManageDialog,
        plan,
        setPlan,
        hasPastDue,
        setHasPastDue,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export { SubscriptionProvider, SubscriptionContext };
